import { ColumnDef, Row } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import dayjs from '@store/timetable/dayjs.config'
import {
  IconButtonCell,
  TextCell,
  TextIconButtonCell,
  UserCardCell,
  UserCell,
} from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import { IGroupLessonPaymentByLessonItem } from '../../utils/types'
import cn from './index.module.css'
import useRoles from '@/hooks/useRoles'
import { DownOutlined, EditOutlined, UpOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useFranchises } from '@/hooks/useFranchises'

interface TableColumnsProps {
  onSeminarianClick: (user: IUser) => void
  onChangeStatusClick: (lesson: IGroupLessonPaymentByLessonItem) => void
  onIncomeClick: (lesson: IGroupLessonPaymentByLessonItem) => void
  showStudentStatus: boolean
}

const formatDateTime = (date: string) => {
  const value = dayjs(date)

  return value.isValid() ? value.format('DD.MM.YYYY HH:mm') : '-'
}

const getProfit = (income?: number | null, salary?: number | null) => {
  if (income === null || income === undefined) return null
  if (salary === null || salary === undefined) return null

  return income - salary
}

const formatComments = (lesson: IGroupLessonPaymentByLessonItem) => {
  if (Array.isArray(lesson.comments) && lesson.comments.length > 0) {
    return lesson.comments.map((item) => ({
      key: `${item.student_id}-${item.comment}`,
      text: `${item.student_name}\n${getStatusDescription(
        String(item.status),
      )}\n${item.comment}`,
    }))
  }

  return lesson.comment ? [{ key: 'comment', text: lesson.comment }] : []
}

const useTableColumns = ({
  onSeminarianClick,
  onChangeStatusClick,
  onIncomeClick,
  showStudentStatus,
}: TableColumnsProps) => {
  const { isAdminOrJuniorAdmin } = useRoles()
  const { enabled: franchisesEnabled } = useFranchises()
  const [expandedComments, setExpandedComments] = useState<
    Record<number, boolean>
  >({})
  const settings = useSelector((store: any) => ({
    teacherCanEdit: store.settings?.isTeacherCanEditStatuses === '1',
  }))

  return useMemo<ColumnDef<IGroupLessonPaymentByLessonItem>[]>(() => {
    const columns: ColumnDef<IGroupLessonPaymentByLessonItem>[] = [
      {
        header: '',
        id: 'index',
        size: 40,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={String(row.original.index ?? row.index + 1)}
            styles={{ textAlign: 'center' }}
          />
        ),
      },
      ...(showStudentStatus
        ? [
            {
              header: 'Статус учащегося',
              id: 'student-status',
              size: 150,
              cell: ({
                row,
              }: {
                row: Row<IGroupLessonPaymentByLessonItem>
              }) => (
                <TextCell
                  text={getStatusDescription(
                    String(row.original.student_status),
                  )}
                  wrapperStyles={{
                    backgroundColor: getStatusColor(
                      String(row.original.student_status),
                    ),
                  }}
                />
              ),
            },
          ]
        : []),
      {
        header: 'Дата и время занятия',
        id: 'date-time',
        size: 170,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={formatDateTime(row.original.date_time)} />
        ),
      },
      {
        header: 'Педагог',
        id: 'seminarian',
        size: 190,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <UserCardCell
            user={row.original.seminarian}
            onClick={() => onSeminarianClick(row.original.seminarian)}
          />
        ),
      },
      {
        header: 'Курс',
        id: 'course',
        size: 180,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={row.original.course?.name ?? '-'}
            styles={{ width: '100%', textAlign: 'start' }}
          />
        ),
      },
      {
        header: 'Группа',
        id: 'group',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={row.original.group?.name ?? '-'}
            styles={{ width: '100%', textAlign: 'start' }}
          />
        ),
      },
      {
        header: 'Филиал',
        id: 'franchise',
        size: 180,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={row.original.franchise?.name ?? '-'} />
        ),
      },
      {
        header: 'Статус',
        id: 'status',
        size: 150,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={getStatusDescription(String(row.original.status))}
            wrapperStyles={{
              backgroundColor: getStatusColor(String(row.original.status)),
            }}
          />
        ),
      },
      {
        header: 'Длительность',
        id: 'duration',
        size: 120,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={row.original.duration ?? '-'} />
        ),
      },
      {
        header: 'Кол-во учащихся в группе',
        id: 'group-student-count',
        size: 170,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.group_student_count ?? '-')} />
        ),
      },
      {
        header: 'Проведено для',
        id: 'visited-count',
        size: 140,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.visited_count ?? '-')} />
        ),
      },
      {
        header: 'Не пришло',
        id: 'missed-count',
        size: 120,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.missed_count ?? '-')} />
        ),
      },
      {
        header: 'Доход',
        id: 'income',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextIconButtonCell
            text={String(row.original.income ?? '-')}
            onClick={async () => onIncomeClick(row.original)}
          />
        ),
      },
      {
        header: 'ЗП',
        id: 'salary',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.salary ?? '-')} />
        ),
      },
      {
        header: 'Прибыль',
        id: 'profit',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={String(
              getProfit(row.original.income, row.original.salary) ?? '-',
            )}
          />
        ),
      },
      {
        header: 'Комментарий',
        id: 'comment',
        size: 300,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => {
          const comments = formatComments(row.original)
          const isExpanded = Boolean(expandedComments[row.original.id])
          const hasOverflow =
            comments.length > 1 || (comments[0]?.text?.length ?? 0) > 80
          const visibleComments = isExpanded ? comments : comments.slice(0, 1)

          if (comments.length === 0) {
            return <TextCell text="-" />
          }

          return (
            <div
              className={cn.commentCell}
              data-expanded={isExpanded ? 'true' : 'false'}
            >
              <div className={cn.commentText}>
                {visibleComments.map((comment) => (
                  <div key={comment.key} className={cn.commentLine}>
                    {comment.text}
                  </div>
                ))}
              </div>
              {hasOverflow && (
                <button
                  type="button"
                  className={cn.commentToggle}
                  onClick={() =>
                    setExpandedComments((prev) => ({
                      ...prev,
                      [row.original.id]: !prev[row.original.id],
                    }))
                  }
                  title={isExpanded ? 'Свернуть' : 'Показать полностью'}
                >
                  {isExpanded ? (
                    <UpOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  ) : (
                    <DownOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  )}
                </button>
              )}
            </div>
          )
        },
      },
      {
        id: 'change-status',
        size: 70,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <IconButtonCell
            onClick={() => onChangeStatusClick(row.original)}
            icon={
              <EditOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
          />
        ),
      },
    ]

    const visibleColumns = franchisesEnabled
      ? columns
      : columns.filter((column) => column.id !== 'franchise')

    if (isAdminOrJuniorAdmin) return visibleColumns

    return visibleColumns
      .filter((column) => !['profit', 'income'].includes(String(column.id)))
      .filter((column) => {
        return String(column.id) === 'change-status'
          ? settings.teacherCanEdit
          : true
      })
  }, [
    onSeminarianClick,
    onChangeStatusClick,
    onIncomeClick,
    isAdminOrJuniorAdmin,
    expandedComments,
    settings,
    franchisesEnabled,
    showStudentStatus,
  ])
}

function getStatusDescription(value: string) {
  switch (value) {
    case '5':
      return 'Проведено'
    case '9':
      return 'Перенесено'
    case '10':
      return 'Отмена учеником'
    case '11':
      return 'Отмена педагогом'
    case '12':
      return 'Платная отмена'
    default:
      return 'Не отчитались'
  }
}

function getStatusColor(value: string) {
  switch (value) {
    case '5':
      return 'var(--color-status-success-border)'
    case '9':
      return 'var(--color-status-warning)'
    case '10':
      return 'var(--color-status-danger-border)'
    case '11':
      return 'var(--color-status-danger-border)'
    case '12':
      return 'var(--color-status-danger-bg)'
    default:
      return 'var(--color-status-warning-border)'
  }
}

export default useTableColumns
